import { Injectable, isDevMode } from '@angular/core';
import { Breadcrumb, Severity, User } from "@sentry/types";
import { ISamContact, IUser } from "libraries/interfaces/IUser";
import { LanguageService } from "libraries/service/language/language.service";
import * as Sentry from "@sentry/browser";

declare const process: any;

export interface ITraceBreadcrumb {
    type?: string;
    level?: TraceSeverity;
    event_id?: string;
    category?: string;
    message?: string;
    data?: {
        [key: string]: unknown;
    };
    timestamp?: number;
}

export enum TraceSeverity {
    Fatal = "fatal",
    Error = "error",
    Warning = "warning",
    Log = "log",
    Info = "info",
    Debug = "debug",
    Critical = "critical"
}

@Injectable()
export class ErrorTracingService {

    constructor(private readonly languageService: LanguageService) {
    }

    public setUser(user?: IUser): void {

        // tslint:disable-next-line:typedef variable-name
        getSentry().then((Sentry): void => {
            if (!Sentry) {
                return;
            }
            // set user language
            Sentry.setTag('user.language', this.languageService.getUserLanguage());

            // no user logged in
            if (!user) {
                Sentry.setUser(null);
                Sentry.setTag('username', null);
                return;
            }

            // user logged in
            const contact: ISamContact = user.sam_contact;
            const sentryUser: User = {
                email: contact.email,
                name: `${contact.firstName} ${contact.lastName ? contact.lastName[0] : null}`,
                id: `${user.sam_uid}`,
                sam_user: user
            };
            Sentry.setUser(sentryUser);
            Sentry.setTag('username', sentryUser.name);
        }).catch(console.warn.bind(console));
    }

    public addBreadcrumb(traceBC: ITraceBreadcrumb): void {
        // tslint:disable-next-line:typedef variable-name
        getSentry().then((Sentry) => {
            if (!Sentry) {
                return;
            }
            const sentryBC: Breadcrumb = {
                ...traceBC,
                level: toSentrySeverity(traceBC.level)
            };
            Sentry.addBreadcrumb(sentryBC);
        }).catch(console.warn.bind(console));
    }

    // tslint:disable-next-line:no-any
    public captureException(e: any): void {
        console.error("Silent exception.", e);

        // tslint:disable-next-line:typedef variable-name
        getSentry().then((Sentry) => {
            if (!Sentry) {
                return;
            }
            if (e.cause) {
                Sentry.setExtra('cause', { name: e.cause.name, message: e.cause.message });
            }
            Sentry.captureException(e);
        }).catch(console.warn.bind(console));
    }

    public captureMessage(msg: string, level?: TraceSeverity): void {
        switch (level) {
            case TraceSeverity.Debug:
                console.debug("Silent message.", msg);
                break;
            case TraceSeverity.Info:
                // tslint:disable-next-line:no-console
                console.info("Silent message.", msg);
                break;
            case TraceSeverity.Log:
                // tslint:disable-next-line:no-console
                console.log("Silent message.", msg);
                break;
            case TraceSeverity.Warning:
                console.warn("Silent message.", msg);
                break;
            case TraceSeverity.Fatal:
            case TraceSeverity.Critical:
            case TraceSeverity.Error:
            default:
                console.error("Silent message.", msg);
                break;
        }

        // tslint:disable-next-line:typedef variable-name
        getSentry().then((Sentry) => {
            if (!Sentry) {
                return;
            }
            Sentry.captureMessage(msg, level ? Severity.fromString(`${level}`) : null);
        }).catch(console.warn.bind(console));
    }
}

function toSentrySeverity(traceSeverity?: TraceSeverity): Severity {
    return traceSeverity ? Severity.fromString(`${traceSeverity}`) : null;
}

// tslint:disable-next-line: no-any
async function getSentry(): Promise<any> {
    if (!isDevMode()) {
        return Promise.resolve(Sentry);
    }
    // Sentry not loaded in Angular dev mode
    return Promise.resolve(null);
}
